const post = {
  slug: 'youtube-video-ideas-that-work',
  cluster: 'topics',
  eyebrow: 'Topics',
  title: 'Six idea shapes that reliably make good videos',
  description:
    'Not a list of topics — a list of structures. Each one produces a video with built-in tension, and you can apply all six to any niche.',
  keywords: [
    'youtube video ideas', 'video idea generation', 'content ideas',
    'youtube video concepts', 'faceless video ideas', 'video topic formats',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/youtube-video-ideas-that-work.webp',
    alt: 'Six labelled templates, each sketching a different narrative arc from setup to resolution.',
  },
  lede:
    'Lists of video ideas go stale immediately and never fit your niche. Idea *shapes* do not: they are structures with tension already built in, and you can point any of them at any subject.',
  sections: [
    {
      heading: '1. The thing that should have worked',
      body: [
        {
          type: 'para',
          text: 'Something with every advantage — money, talent, timing, a head start — that failed anyway. The tension is automatic: the viewer knows the outcome contradicts the setup and wants the mechanism.',
        },
        {
          type: 'para',
          text: 'Works in any niche with named actors. Strong visually, because failures are documented — there are photographs, filings, contemporary coverage.',
        },
      ],
    },
    {
      heading: '2. The decision nobody noticed',
      body: [
        {
          type: 'para',
          text: 'A small, boring choice that turned out to determine everything downstream. A standard nobody voted on, a default setting, a line in a contract.',
        },
        {
          type: 'para',
          text: 'This shape is strong because the payoff genuinely surprises: the viewer arrives thinking the cause was the obvious big thing and leaves knowing it was not. It also tends to be underserved, since the obvious cause is what everyone else made a video about.',
        },
      ],
    },
    {
      heading: '3. Why it is like that',
      body: [
        {
          type: 'para',
          text: 'Something in plain sight with a strange property everyone has stopped noticing. Why train tracks are that width, why the keyboard is in that order, why a form asks for that field.',
        },
        {
          type: 'para',
          text: 'The hook does the work — once the oddity is pointed out it cannot be un-noticed. Be careful here: these topics attract tidy stories that are widely repeated and frequently wrong. Verify before committing, or the video joins the pile of confidently incorrect ones.',
        },
      ],
    },
    {
      heading: '4. The two that look the same and are not',
      body: [
        {
          type: 'para',
          text: 'A comparison where the interesting part is that the difference is not what the viewer assumed. Two competitors, two approaches, two standards.',
        },
        {
          type: 'para',
          text: 'Structurally strong for faceless video because comparison is inherently visual — side-by-side, chart, table — so the pictures have something to do throughout.',
        },
      ],
    },
    {
      heading: '5. What it actually takes',
      body: [
        {
          type: 'para',
          text: 'The unglamorous full accounting of a process everyone has a vague idea about: what it costs, how long it takes, how many people, what goes wrong. Scale is the payoff.',
        },
        {
          type: 'para',
          text: 'This shape lives on real figures, which makes it strong when you have them and unmakeable when you do not. Do not fill the gaps with plausible-sounding numbers — see [handling uncertain facts](/blogs/handling-uncertain-facts-in-scripts).',
        },
      ],
    },
    {
      heading: '6. The moment it changed',
      body: [
        {
          type: 'para',
          text: 'A specific date, decision or event where a before and an after are genuinely different. Not a history of a thing — the hinge.',
        },
        {
          type: 'para',
          text: 'Naturally satisfies the "what changes between the first line and the last" test that most videos fail, because the change is the premise.',
        },
      ],
    },
    {
      heading: 'Using them',
      body: [
        {
          type: 'para',
          text: 'Take your niche and run all six against it in one sitting. You will get more usable ideas in an hour than from a month of waiting for inspiration, and they will be structurally sound rather than merely on-topic.',
        },
        {
          type: 'para',
          text: 'Then run each survivor through the three-part test in [topic research](/blogs/youtube-topic-research): would anyone ask this, is the answer non-obvious, can you actually answer it.',
        },
        {
          type: 'callout',
          title: 'The shape is not the video',
          text: 'These get you a structurally promising idea. They do not substitute for the research that makes it specific — a "thing that should have worked" video with no names and no figures is as generic as anything else.',
        },
      ],
    },
  ],
  related: ['youtube-topic-research', 'youtube-competitor-research', 'youtube-video-script-structure'],
};

export default post;
